import { useEffect } from 'react';

import { useSearchParams } from '@shared/hooks/useSearchParams.hook';
import {
  DEFAULT_PAGINATION_PAGE,
  PAGINATION_PAGE_PARAM,
  PAGINATION_PAGE_SIZE_PARAM,
} from '@shared/constants/pagination.constants';

import PaginationControls from './PaginationControls';
import { calculateRowsPerPage } from '../helpers/calculateRowsPerPage';
import toPositiveInteger from '../helpers/toPositiveInteger';

type TBaseProps = {
  total: number;
  maxPageSize?: number;
  margin?: number;
  showTotal?: boolean;
  isFetching?: boolean;
  ariaLabel?: string;
};

type TControlledProps = {
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
};

type TUncontrolledProps = {
  page?: never;
  pageSize?: never;
  onPageChange?: never;
  onPageSizeChange?: never;
};

type TProps = TBaseProps & (TControlledProps | TUncontrolledProps);

const UrlPagination = ({ total, margin, showTotal, isFetching, ariaLabel, maxPageSize }: TBaseProps) => {
  const { searchParams, changeMany } = useSearchParams();
  const rawPage = searchParams.get(PAGINATION_PAGE_PARAM);
  const rawPageSize = searchParams.get(PAGINATION_PAGE_SIZE_PARAM);
  const pageSize = Math.min(
    maxPageSize ?? Infinity,
    toPositiveInteger(Number(rawPageSize), Number(calculateRowsPerPage(margin)))
  );
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const requestedPage = toPositiveInteger(Number(rawPage), DEFAULT_PAGINATION_PAGE);
  const page = isFetching ? requestedPage : Math.min(requestedPage, totalPages);
  const displayedPageSize = total > 0 ? Math.min(pageSize, total) : pageSize;

  useEffect(() => {
    if (rawPage !== String(page) || rawPageSize !== String(pageSize))
      changeMany({ [PAGINATION_PAGE_PARAM]: String(page), [PAGINATION_PAGE_SIZE_PARAM]: String(pageSize) });
  }, [rawPage, rawPageSize, page, pageSize, changeMany]);

  useEffect(() => {
    const handleResize = () => {
      const nextSize = Math.min(maxPageSize ?? Infinity, Number(calculateRowsPerPage(margin)));
      if (nextSize === pageSize) return;
      changeMany({
        [PAGINATION_PAGE_SIZE_PARAM]: String(nextSize),
        [PAGINATION_PAGE_PARAM]: String(Math.min(page, Math.max(1, Math.ceil(total / nextSize)))),
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [margin, maxPageSize, page, pageSize, total, changeMany]);

  return (
    <PaginationControls
      total={total}
      page={page}
      pageSize={displayedPageSize}
      maxPageSize={maxPageSize}
      showTotal={showTotal}
      isFetching={isFetching}
      ariaLabel={ariaLabel}
      deferPageSizeChange
      onPageChange={(nextPage) => changeMany({ [PAGINATION_PAGE_PARAM]: String(nextPage) })}
      onPageSizeChange={(nextSize) =>
        changeMany({ [PAGINATION_PAGE_PARAM]: '1', [PAGINATION_PAGE_SIZE_PARAM]: String(nextSize) })
      }
    />
  );
};

export const Pagination = (props: TProps) => {
  if (props.page === undefined) return <UrlPagination {...props} />;

  return (
    <PaginationControls
      total={props.total}
      page={props.page}
      pageSize={props.pageSize}
      showTotal={props.showTotal}
      maxPageSize={props.maxPageSize}
      isFetching={props.isFetching}
      ariaLabel={props.ariaLabel}
      deferPageSizeChange
      onPageChange={props.onPageChange}
      onPageSizeChange={props.onPageSizeChange}
    />
  );
};
